import {
  CommonListModel,
  PwmProfileExchangeItemCreateDocument,
  PwmProfileExchangeItemDeleteDocument,
  PwmProfileExchangeItemGetByIdDocument,
  PwmProfileExchangeItemListDocument,
  PwmProfileExchangeItemListPaginatedDocument,
  PwmProfileExchangeItemUpdateDocument,
} from "api/generated";
import { RequestTypes } from "api/services/enums";
import { getRequestInstance } from "api/services/utils";
import { PartialNullable } from "config";
import { executeEviction } from "api/utils/cache-eviction";
import { client } from "config/apollo";

import {
  IPwmProfileExchangeItemCreateVars,
  IPwmProfileExchangeItemModel,
  IPwmProfileExchangeItemPaginatedResponse,
  IPwmProfileExchangeItemPaginatedVars,
  IPwmProfileExchangeItemUpdateVars,
} from "./types";

export async function getPwmProfileExchangeItemPaginated(
  variables: IPwmProfileExchangeItemPaginatedVars
): Promise<IPwmProfileExchangeItemPaginatedResponse> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: PwmProfileExchangeItemListPaginatedDocument,
      variables,
      fetchPolicy: "network-only",
    });

    return res?.data?.pwmProfileExchangeItemListPaginated;
  } catch (error) {
    throw error;
  }
}

export async function createPwmProfileExchangeItem(
  variables: IPwmProfileExchangeItemCreateVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: PwmProfileExchangeItemCreateDocument,
      variables,
      fetchPolicy: "network-only",
    });

    // Evict own lists
    executeEviction(client, {
      typename: "PwmProfileExchangeItem",
      fieldNames: [
        "pwmProfileExchangeItemListPaginated",
        "pwmProfileExchangeItemList",
      ],
    });

    // Evict all parent PwmProfileExchange entities (they reference these items)
    executeEviction(client, {
      typename: "PwmProfileExchange",
      fieldNames: [
        "pwmProfileExchangeListPaginated",
        "pwmProfileExchangeGetById",
      ],
    });

    return res?.data?.pwmProfileExchangeItemCreate ?? false;
  } catch (error) {
    throw error;
  }
}

export async function updatePwmProfileExchangeItem(
  variables: IPwmProfileExchangeItemUpdateVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: PwmProfileExchangeItemUpdateDocument,
      variables,
      fetchPolicy: "network-only",
    });

    // Evict updated entity + own lists + getById
    executeEviction(client, {
      typename: "PwmProfileExchangeItem",
      id: variables.id.toString(),
      fieldNames: [
        "pwmProfileExchangeItemListPaginated",
        "pwmProfileExchangeItemList",
        "pwmProfileExchangeItemGetById",
      ],
    });

    // Evict all parent PwmProfileExchange entities (they reference these items)
    executeEviction(client, {
      typename: "PwmProfileExchange",
      fieldNames: [
        "pwmProfileExchangeListPaginated",
        "pwmProfileExchangeGetById",
      ],
    });

    return res?.data?.pwmProfileExchangeItemUpdate ?? false;
  } catch (error) {
    throw error;
  }
}

export async function getPwmProfileExchangeItem(
  id: number
): Promise<PartialNullable<IPwmProfileExchangeItemModel>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: PwmProfileExchangeItemGetByIdDocument,
      variables: {
        id,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.pwmProfileExchangeItemGetById;
  } catch (error) {
    throw error;
  }
}

export async function deletePwmProfileExchangeItem(
  id: number
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: PwmProfileExchangeItemDeleteDocument,
      variables: {
        id,
      },
      fetchPolicy: "no-cache",
    });

    // Evict deleted entity + own lists
    executeEviction(client, {
      typename: "PwmProfileExchangeItem",
      id: id.toString(),
      fieldNames: [
        "pwmProfileExchangeItemListPaginated",
        "pwmProfileExchangeItemList",
      ],
    });

    // Evict all parent PwmProfileExchange entities (they reference these items)
    executeEviction(client, {
      typename: "PwmProfileExchange",
      fieldNames: [
        "pwmProfileExchangeListPaginated",
        "pwmProfileExchangeGetById",
      ],
    });

    return Boolean(res?.data?.pwmProfileExchangeItemDelete);
  } catch (error) {
    throw error;
  }
}

export async function getPwmProfileExchangeItemList(): Promise<
  CommonListModel[]
> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: PwmProfileExchangeItemListDocument,
      fetchPolicy: "network-only",
    });

    return res?.data?.pwmProfileExchangeItemList;
  } catch (error) {
    throw error;
  }
}
